import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useClient } from '@/hooks/useClient'
import type { OrpcClient } from '@/orpc/client'

export type CareJourneysData = Awaited<ReturnType<OrpcClient['careJourneys']>>
export type CareJourney = CareJourneysData['journeys'][number]
export type JourneyVisit = CareJourney['visits'][number]
export type JourneyCell = JourneyVisit['visit']
export type JourneyKpis = CareJourneysData['kpis']
export type JourneyKpi = CareJourney['kpis'][number]
export type JourneyReason = CareJourney['reasons'][number]
export type JourneyFilters = Parameters<OrpcClient['careJourneys']>[0]

/**
 * Tailwind classes per status tone. Every use pairs these with the cell's own
 * text, never colour alone — a WCAG requirement the ticket calls out explicitly
 * ("Do not rely on color alone").
 */
export const TONE_TEXT: Record<JourneyCell['tone'], string> = {
  green: 'text-emerald-600',
  blue: 'text-blue-600',
  red: 'text-red-600',
  // gray-500 (#6B7280), not gray-400 (#9CA3AF): this is the PRIMARY label of
  // every "not required"/"not available" cell, and gray-400 on white is ~2.8:1,
  // under the 4.5:1 WCAG AA floor. gray-500 clears it at ~4.8:1.
  gray: 'text-gray-500',
}

export const TONE_DOT: Record<JourneyCell['tone'], string> = {
  green: 'bg-emerald-500',
  blue: 'bg-blue-500',
  red: 'bg-red-500',
  gray: 'bg-gray-300',
}

export const TONE_PILL: Record<JourneyCell['tone'], string> = {
  green: 'bg-emerald-50 text-emerald-700',
  blue: 'bg-blue-50 text-blue-700',
  red: 'bg-red-50 text-red-700',
  gray: 'bg-gray-100 text-gray-600',
}

export const DEFAULT_JOURNEY_FILTERS: JourneyFilters = {}

/** Filters the user has actually changed — drives the "Filters" button badge. */
export function activeJourneyFilterCount(filters: JourneyFilters): number {
  if (!filters) return 0
  return Object.entries(filters).filter(([, value]) => {
    if (value === undefined || value === null || value === '') return false
    if (Array.isArray(value)) return value.length > 0
    if (value === false) return false
    return true
  }).length
}

export function useCareJourneys(filters: JourneyFilters) {
  const client = useClient()
  return useQuery({
    queryKey: ['care-journeys', filters],
    queryFn: () => client.careJourneys(filters),
    // Keep the previous page visible while a filter change is in flight, so the
    // table doesn't collapse to a spinner on every keystroke.
    placeholderData: keepPreviousData,
  })
}
